import type { HttpClient } from "./client";

export interface SseFrame {
  event: string;
  data: string;
}

/**
 * Reads a server-sent event stream with fetch, because the token must ride in
 * a header and EventSource cannot send one (docs/api.md 38.9). Resolves when
 * the server closes the stream; rejects on a failed open or a dropped
 * connection. Keep-alive comments are skipped.
 */
export async function readEventStream(
  http: HttpClient,
  path: string,
  onFrame: (frame: SseFrame) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await http.raw("GET", path, { signal, timeoutMs: 0, accept: "text/event-stream" });
  if (!res.body) throw new Error("The stream has no body.");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let end: number;
    while ((end = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const frame = parseFrame(raw);
      if (frame) onFrame(frame);
    }
  }
}

export function parseFrame(raw: string): SseFrame | null {
  let event = "message";
  const data: string[] = [];
  for (const line of raw.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? "" : line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }
  return data.length ? { event, data: data.join("\n") } : null;
}
